import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getAdminDashboard, 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser,
  downloadComplianceCSV,
  sendReminders,
  getPendingUsers,
  approveUser,
  rejectUser,
  getOnboardingStats,
  getAdminDashboardSummary
} from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Users, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Bell,
  Loader2,
  LogOut,
  User,
  LayoutDashboard,
  FileText,
  Sprout,
  UserPlus,
  UserCheck,
  UserX,
  Shield,
  ClipboardCheck,
  ChevronRight,
  DollarSign,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [schedulingSummary, setSchedulingSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, usersRes, pendingRes, obStatsRes, schedSummaryRes] = await Promise.all([
        getAdminDashboard(),
        getUsers(),
        getPendingUsers(),
        getOnboardingStats().catch(() => ({ data: null })),
        getAdminDashboardSummary().catch(() => ({ data: null }))
      ]);
      setDashboard(dashboardRes.data);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setOnboardingStats(obStatsRes.data);
      setSchedulingSummary(schedSummaryRes.data);
    } catch (err) {
      setError('Failed to load dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      await createUser(userForm);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', role: 'staff' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await updateUser(editingUser.id, {
        name: userForm.name,
        email: userForm.email,
        role: userForm.role
      });
      toast.success('User updated successfully');
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'staff' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(userToDelete.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleExportCSV = async () => {
    try {
      await downloadComplianceCSV();
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await sendReminders();
      toast.success(response.data.message);
    } catch (err) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleApproveUser = async (userId) => {
    setApprovingId(userId);
    try {
      const res = await approveUser(userId);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve user');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectUser = async (userId) => {
    setRejectingId(userId);
    try {
      const res = await rejectUser(userId);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject user');
    } finally {
      setRejectingId(null);
    }
  };

  const openEditDialog = (userItem) => {
    setEditingUser(userItem);
    setUserForm({ name: userItem.name, email: userItem.email, password: '', role: userItem.role });
    setUserDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'staff' });
    setUserDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { overall_compliance, total_staff, total_modules, staff_compliance, expired_training, due_soon_training } = dashboard;

  return (
    <div className="min-h-screen bg-[#F0FAF7]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#A8E6CF]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center shadow-sm">
                <Sprout className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h1 className="text-sm sm:text-lg font-bold text-[#2D4A4A]">Growing Minds</h1>
                <p className="text-[10px] sm:text-xs text-[#5A7A7A]">Admin Dashboard</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/admin/onboarding">
                <Button variant="outline" size="sm" data-testid="onboarding-management-link" className="hidden sm:flex border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                  <FileText className="w-4 h-4 mr-2" />
                  Onboarding
                </Button>
                <Button variant="outline" size="sm" data-testid="onboarding-management-link-mobile" className="flex sm:hidden p-2 border-[#A8E6CF] text-[#5BA4A4]">
                  <FileText className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/tutors">
                <Button variant="outline" size="sm" data-testid="tutor-management-link" className="hidden sm:flex border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Tutors & Pay
                </Button>
                <Button variant="outline" size="sm" data-testid="tutor-management-link-mobile" className="flex sm:hidden p-2 border-[#A8E6CF] text-[#5BA4A4]">
                  <DollarSign className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/scheduling">
                <Button variant="outline" size="sm" data-testid="scheduling-link" className="hidden sm:flex border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                  <Calendar className="w-4 h-4 mr-2" />
                  Scheduling
                </Button>
                <Button variant="outline" size="sm" data-testid="scheduling-link-mobile" className="flex sm:hidden p-2 border-[#A8E6CF] text-[#5BA4A4]">
                  <Calendar className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/admin/timesheets">
                <Button variant="outline" size="sm" data-testid="timesheets-link" className="hidden sm:flex border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]">
                  <FileText className="w-4 h-4 mr-2" />
                  Timesheets
                </Button>
                <Button variant="outline" size="sm" data-testid="timesheets-link-mobile" className="flex sm:hidden p-2 border-[#A8E6CF] text-[#5BA4A4]">
                  <FileText className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" size="sm" data-testid="staff-dashboard-link" className="hidden sm:flex">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  My Training
                </Button>
                <Button variant="outline" size="sm" data-testid="staff-dashboard-link-mobile" className="flex sm:hidden p-2">
                  <LayoutDashboard className="w-4 h-4" />
                </Button>
              </Link>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="user-menu-button" className="p-2 sm:px-3">
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Admin Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-gray-500">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-[#5BA4A4] cursor-pointer" data-testid="admin-profile-link">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#2D4A4A]">Compliance Overview</h2>
            <p className="text-sm sm:text-base text-[#5A7A7A]">Monitor staff training</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={handleSendReminders}
              disabled={sendingReminders}
              data-testid="send-reminders-button"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm"
            >
              {sendingReminders ? (
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
              ) : (
                <Bell className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Send </span>Reminders
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportCSV}
              data-testid="export-csv-button"
              size="sm"
              className="flex-1 sm:flex-none text-xs sm:text-sm"
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Export </span>CSV
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <Card className="border border-[#A8E6CF]/30">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-[#5A7A7A]">Compliance</p>
                  <p className="text-xl sm:text-3xl font-bold text-[#5BA4A4]">{overall_compliance}%</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-[#5BA4A4]" />
                </div>
              </div>
              <Progress value={overall_compliance} className="mt-2 sm:mt-3 h-1.5 sm:h-2" />
            </CardContent>
          </Card>

          <Card className="border border-[#A8E6CF]/30">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-[#5A7A7A]">Staff</p>
                  <p className="text-xl sm:text-3xl font-bold text-[#2D4A4A]">{total_staff}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-amber-700">Due Soon</p>
                  <p className="text-xl sm:text-3xl font-bold text-amber-800">{due_soon_training.length}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-amber-200 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-red-200 bg-red-50">
            <CardContent className="p-3 sm:pt-6 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-red-700">Expired</p>
                  <p className="text-xl sm:text-3xl font-bold text-red-800">{expired_training.length}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-200 rounded-full flex items-center justify-center">
                  <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Onboarding Quick Stats */}
        {onboardingStats && (
          <Card className="mb-4 sm:mb-8 border border-[#A8E6CF]/30 bg-white" data-testid="onboarding-stats-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#5BA4A4]" />
                  <h3 className="text-sm font-semibold text-[#2D4A4A]">Safer Recruitment</h3>
                </div>
                <Link to="/admin/onboarding">
                  <Button variant="ghost" size="sm" className="text-xs text-[#5BA4A4] hover:bg-[#F0FAF7]" data-testid="view-onboarding-link">
                    View Details <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center p-2 rounded-lg bg-emerald-50">
                  <p className="text-lg font-bold text-emerald-700">{onboardingStats.fully_onboarded}</p>
                  <p className="text-[10px] text-emerald-600">Fully Onboarded</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-red-50">
                  <p className="text-lg font-bold text-red-600">{onboardingStats.not_onboarded}</p>
                  <p className="text-[10px] text-red-500">Not Onboarded</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-amber-50">
                  <p className="text-lg font-bold text-amber-700">{onboardingStats.pending_sections}</p>
                  <p className="text-[10px] text-amber-600">Sections Awaiting Review</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-[#E8F8F5]">
                  <p className="text-lg font-bold text-[#5BA4A4]">{onboardingStats.total_staff}</p>
                  <p className="text-[10px] text-[#5A7A7A]">Total Staff</p>
                </div>
              </div>
              {/* DBS/RTW Expiry Alerts */}
              {((onboardingStats.dbs_expiring > 0) || (onboardingStats.dbs_expired > 0) || (onboardingStats.rtw_expiring > 0) || (onboardingStats.rtw_expired > 0)) && (
                <div className="mt-3 p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid="expiry-alerts-banner">
                  <p className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Compliance Expiry Alerts
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {onboardingStats.dbs_expired > 0 && (
                      <span className="text-red-700 font-bold">{onboardingStats.dbs_expired} DBS expired</span>
                    )}
                    {onboardingStats.dbs_expiring > 0 && (
                      <span className="text-amber-700">{onboardingStats.dbs_expiring} DBS expiring (90d)</span>
                    )}
                    {onboardingStats.rtw_expired > 0 && (
                      <span className="text-red-700 font-bold">{onboardingStats.rtw_expired} RTW expired</span>
                    )}
                    {onboardingStats.rtw_expiring > 0 && (
                      <span className="text-amber-700">{onboardingStats.rtw_expiring} RTW expiring (90d)</span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scheduling & Payroll Summary */}
        {schedulingSummary && (
          <Card className="mb-4 sm:mb-8 border border-[#A8E6CF]/30 bg-white" data-testid="scheduling-summary-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#5BA4A4]" />
                  <h3 className="text-sm font-semibold text-[#2D4A4A]">Scheduling & Payroll</h3>
                </div>
                <div className="flex gap-2">
                  <Link to="/admin/scheduling">
                    <Button variant="ghost" size="sm" className="text-xs text-[#5BA4A4] hover:bg-[#F0FAF7]" data-testid="view-scheduling-link">
                      Schedules <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/admin/timesheets">
                    <Button variant="ghost" size="sm" className="text-xs text-[#5BA4A4] hover:bg-[#F0FAF7]" data-testid="view-timesheets-link">
                      Payroll <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="text-center p-2 rounded-lg bg-[#E8F8F5]">
                  <p className="text-lg font-bold text-[#5BA4A4]">{schedulingSummary.schedule?.tutors_scheduled || 0}</p>
                  <p className="text-[10px] text-[#5A7A7A]">Tutors This Week</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-emerald-50">
                  <p className="text-lg font-bold text-emerald-700">{schedulingSummary.schedule?.confirmed || 0}</p>
                  <p className="text-[10px] text-emerald-600">Confirmed Sessions</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-amber-50">
                  <p className="text-lg font-bold text-amber-700">{schedulingSummary.schedule?.pending || 0}</p>
                  <p className="text-[10px] text-amber-600">Pending Sessions</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-red-50">
                  <p className="text-lg font-bold text-red-600">{schedulingSummary.schedule?.released || 0}</p>
                  <p className="text-[10px] text-red-500">Released</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="text-center p-2 rounded-lg bg-sky-50">
                  <p className="text-lg font-bold text-sky-700">{schedulingSummary.payroll?.total_hours || 0}h</p>
                  <p className="text-[10px] text-sky-600">Hours This Month</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-emerald-50">
                  <p className="text-lg font-bold text-emerald-700">£{(schedulingSummary.payroll?.paid || 0).toFixed(2)}</p>
                  <p className="text-[10px] text-emerald-600">Paid</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-amber-50">
                  <p className="text-lg font-bold text-amber-700">£{(schedulingSummary.payroll?.outstanding || 0).toFixed(2)}</p>
                  <p className="text-[10px] text-amber-600">Outstanding</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="compliance" className="space-y-4 sm:space-y-6">
          <TabsList className="w-full sm:w-auto flex">
            <TabsTrigger value="compliance" data-testid="tab-compliance" className="flex-1 sm:flex-none text-xs sm:text-sm">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Compliance</span>
              <span className="sm:hidden">Report</span>
            </TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-staff" className="flex-1 sm:flex-none text-xs sm:text-sm">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Staff
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts" className="flex-1 sm:flex-none text-xs sm:text-sm">
              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Alerts </span>({expired_training.length + due_soon_training.length})
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending" className="flex-1 sm:flex-none text-xs sm:text-sm relative">
              <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Pending</span>
              <span className="sm:hidden">New</span>
              {pendingUsers.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {pendingUsers.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <Card className="border border-[#A8E6CF]/30">
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Staff Compliance Status</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Individual training completion</CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Name</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Email</TableHead>
                        <TableHead className="text-xs sm:text-sm">Progress</TableHead>
                        <TableHead className="text-xs sm:text-sm">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staff_compliance.map((staff) => (
                        <TableRow key={staff.id}>
                          <TableCell className="font-medium text-xs sm:text-sm py-2 sm:py-4">{staff.name}</TableCell>
                          <TableCell className="text-[#5A7A7A] text-xs sm:text-sm hidden sm:table-cell">{staff.email}</TableCell>
                          <TableCell className="py-2 sm:py-4">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <Progress value={staff.compliance_percentage} className="w-12 sm:w-20 h-1.5 sm:h-2" />
                              <span className="text-[10px] sm:text-sm font-medium">{staff.compliance_percentage}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 sm:py-4">
                            {staff.expired_count > 0 ? (
                              <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-[10px] sm:text-xs">
                                {staff.expired_count} Exp
                              </Badge>
                            ) : staff.due_soon_count > 0 ? (
                              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] sm:text-xs">
                                {staff.due_soon_count} Due
                              </Badge>
                            ) : staff.compliance_percentage === 100 ? (
                              <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] sm:text-xs">
                                OK
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-100 text-gray-800 border-[#A8E6CF]/30 text-[10px] sm:text-xs">
                                WIP
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Management Tab */}
          <TabsContent value="staff">
            <Card className="border border-[#A8E6CF]/30">
              <CardHeader className="p-3 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                  <div>
                    <CardTitle className="text-base sm:text-lg">Staff Members</CardTitle>
                    <CardDescription className="text-xs sm:text-sm">Manage staff accounts</CardDescription>
                  </div>
                  <Button onClick={openCreateDialog} data-testid="add-staff-button" size="sm" className="text-xs sm:text-sm w-full sm:w-auto">
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Add Staff
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Name</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Email</TableHead>
                        <TableHead className="text-xs sm:text-sm">Role</TableHead>
                        <TableHead className="w-[50px] sm:w-[70px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium text-xs sm:text-sm py-2 sm:py-4">{u.name}</TableCell>
                          <TableCell className="text-[#5A7A7A] text-xs sm:text-sm hidden sm:table-cell">{u.email}</TableCell>
                          <TableCell className="py-2 sm:py-4">
                            <Badge variant="outline" className={`text-[10px] sm:text-xs ${
                              u.role === 'admin' 
                                ? 'bg-purple-100 text-purple-800 border-purple-200' 
                                : 'bg-sky-100 text-sky-700 border-sky-200'
                            }`}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2 sm:py-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${u.id}`} className="p-1 sm:p-2">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(u)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setUserToDelete(u);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <div className="space-y-6">
              {/* Expired Training */}
              <Card className="border border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <XCircle className="w-5 h-5" />
                    Expired Training ({expired_training.length})
                  </CardTitle>
                  <CardDescription>Training modules that have expired and require immediate attention</CardDescription>
                </CardHeader>
                <CardContent>
                  {expired_training.length === 0 ? (
                    <p className="text-[#5A7A7A] text-center py-4">No expired training</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staff Member</TableHead>
                          <TableHead>Module</TableHead>
                          <TableHead>Expired Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expired_training.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.user_name}</TableCell>
                            <TableCell>{item.module_title}</TableCell>
                            <TableCell className="text-red-600">
                              {new Date(item.expired_at).toLocaleDateString('en-GB')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Due Soon Training */}
              <Card className="border border-amber-200">
                <CardHeader>
                  <CardTitle className="text-amber-800 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Due Soon ({due_soon_training.length})
                  </CardTitle>
                  <CardDescription>Training modules expiring within 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {due_soon_training.length === 0 ? (
                    <p className="text-[#5A7A7A] text-center py-4">No training due soon</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staff Member</TableHead>
                          <TableHead>Module</TableHead>
                          <TableHead>Expires</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {due_soon_training.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{item.user_name}</TableCell>
                            <TableCell>{item.module_title}</TableCell>
                            <TableCell className="text-amber-600">
                              {new Date(item.expires_at).toLocaleDateString('en-GB')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <Card className="border border-[#A8E6CF]/30">
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#5BA4A4]" />
                  Pending Registrations ({pendingUsers.length})
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Staff members who have registered and are awaiting approval
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-8 text-[#5A7A7A]">
                    <UserCheck className="w-12 h-12 mx-auto mb-3 text-[#A8E6CF]" />
                    <p className="text-sm">No pending registrations</p>
                  </div>
                ) : (
                  <div className="space-y-3 px-3 sm:px-0 pb-3 sm:pb-0">
                    {pendingUsers.map((pu) => (
                      <div key={pu.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-lg border border-[#A8E6CF]/30 bg-[#F0FAF7]/50 gap-3">
                        <div>
                          <p className="font-medium text-sm text-[#2D4A4A]" data-testid={`pending-name-${pu.id}`}>{pu.name}</p>
                          <p className="text-xs text-[#5A7A7A]">{pu.email}</p>
                          <p className="text-[10px] text-[#5A7A7A] mt-1">
                            Registered: {new Date(pu.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(pu.id)}
                            disabled={approvingId === pu.id || rejectingId === pu.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-1 sm:flex-none"
                            data-testid={`approve-user-${pu.id}`}
                          >
                            {approvingId === pu.id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <UserCheck className="w-3 h-3 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectUser(pu.id)}
                            disabled={approvingId === pu.id || rejectingId === pu.id}
                            className="border-red-300 text-red-600 hover:bg-red-50 text-xs flex-1 sm:flex-none"
                            data-testid={`reject-user-${pu.id}`}
                          >
                            {rejectingId === pu.id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <UserX className="w-3 h-3 mr-1" />
                            )}
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
            <DialogDescription>
              {editingUser 
                ? 'Update the staff member details below' 
                : 'Enter the details for the new staff member'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="Full name"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="user-email-input"
              />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Password"
                  data-testid="user-password-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              data-testid="save-user-button"
            >
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Staff Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              data-testid="confirm-delete-button"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
